import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getItemsPerWord, getProductsByStatus } from "../../../server/Product";
import { getBySlug } from "../../../server/Category";
import { setBaseProducts, reload } from "../../../server/ProductsFilters";
import { extractProductsFromTree } from "./useCategoryUtils";

const getProductId = (product) => String(product.id || product.productId);

const dedupeProducts = (products) =>
  Object.values(
    products.reduce((acc, product) => {
      acc[getProductId(product)] = product;
      return acc;
    }, {}),
  );

const intersectProductGroups = (groups) => {
  if (!groups.length) return null;
  const [first, ...rest] = groups;
  if (!rest.length) return first;

  return first.filter((product) => {
    const id = getProductId(product);
    return rest.every((group) =>
      group.some((item) => getProductId(item) === id),
    );
  });
};

const fetchProductsByIds = async (stationId, ids, fetcher) => {
  const results = await Promise.all(
    ids.map((id) => fetcher(id).catch(() => [])),
  );
  return dedupeProducts(results.flat());
};

const fetchCategoryProducts = async (stationId, categoryChips) => {
  const results = await Promise.all(
    categoryChips.map(({ slug, path }) => {
      if (!slug) return Promise.resolve([]);
      return getBySlug(stationId, slug, false, true)
        .then((node) =>
          extractProductsFromTree(
            node,
            path
              .slice(0, -1)
              .reverse()
              .map((p) => ({ name: p.label, slug: p.slug })),
          ),
        )
        .catch(() => []);
    }),
  );
  return dedupeProducts(results.flat());
};

export const useProductsFilters = (
  stationId,
  statusFilter = [],
  isOrphanView = false,
) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.productFilters.filters);
  const baseProducts = useSelector(
    (state) => state.productFilters.baseProducts,
  );
  const categoryChips = useSelector(
    (state) => state.productFilters.filters.categoryChips,
  );

  useEffect(() => {
    const run = async () => {
      const hasCategoryFilters = categoryChips.length > 0;
      const hasStatusFilter = statusFilter.length > 0;

      const hasSearch = filters.search?.trim().length > 0;

      if (
        !filters.brandIds.length &&
        !filters.subBrandIds.length &&
        !filters.gtin &&
        !hasCategoryFilters &&
        !hasStatusFilter
      ) {
        if (!hasSearch && !isOrphanView) {
          dispatch(setBaseProducts([]));
        }
        return;
      }

      dispatch(reload());

      const groups = [];

      if (hasCategoryFilters) {
        groups.push(await fetchCategoryProducts(stationId, categoryChips));
      }

      if (filters.brandIds.length > 0) {
        groups.push(
          await fetchProductsByIds(stationId, filters.brandIds, (id) =>
            getProductsByStatus(stationId, { brandId: id }),
          ),
        );
      }

      if (filters.subBrandIds.length > 0) {
        groups.push(
          await fetchProductsByIds(stationId, filters.subBrandIds, (id) =>
            getProductsByStatus(stationId, { subBrandId: id }),
          ),
        );
      }

      if (filters.gtin) {
        const gtinRes = await getProductsByStatus(stationId, {
          gtin: filters.gtin.replace(/^0+/, ""),
        }).catch(() => []);
        if (!gtinRes?.length) {
          dispatch(setBaseProducts([]));
          return;
        }
        groups.push(gtinRes);
      }

      if (hasStatusFilter) {
        groups.push(
          await fetchProductsByIds(stationId, statusFilter, (status) =>
            getProductsByStatus(stationId, { status }),
          ),
        );
      }

      const result = intersectProductGroups(groups) ?? [];
      dispatch(setBaseProducts(result));
    };

    run();
  }, [dispatch, stationId, filters, categoryChips, statusFilter, isOrphanView]);

  useEffect(() => {
    const term = filters.search?.trim() ?? "";
    const hasOtherFilters =
      filters.brandIds.length > 0 ||
      filters.subBrandIds.length > 0 ||
      filters.gtin ||
      categoryChips.length > 0 ||
      statusFilter.length > 0;

    if (term.length <= 1 || hasOtherFilters) {
      return;
    }

    dispatch(reload());
    dispatch(
      getItemsPerWord(
        term,
        stationId,
        "gtin,name,variant,brand,sub_brand,user_company,description,kosher_supervision,weight_details,weight_jump,verify_age",
        false,
      ),
    );
  }, [
    filters.search,
    filters.brandIds,
    filters.subBrandIds,
    filters.gtin,
    categoryChips,
    statusFilter,
    dispatch,
    stationId,
  ]);

  return baseProducts;
};
